/**
 * Cart drawer promo — click-to-copy handler.
 *
 * On click, writes the promo code to the clipboard via
 * navigator.clipboard.writeText (with a textarea + execCommand fallback for
 * older / insecure contexts). The customer pastes the code in Shopify's
 * checkout discount field. No fetch, no discount cookie, no navigation
 * hijack, no sessionStorage memory.
 *
 * The "Copied" state is transient — auto-reverts to "Copy code" after 2s so
 * the customer can copy again if needed.
 *
 * Analytics fan-out is preserved: window.dataLayer, window.dluxJourney,
 * and window.fbq (for promo_copy_click only). promo_view still fires once
 * per cart-drawer open.
 */
(function () {
  'use strict';

  var PROMO_LOCATION = 'cart_drawer';

  // Analytics fan-out: GTM dataLayer + Meta Pixel + self-hosted journey
  // tracker (window.dluxJourney from assets/journey-tracker.js).
  function pushAnalytics(type, data) {
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        var payload = { event: type, promo_location: PROMO_LOCATION };
        for (var k in data) {
          if (Object.prototype.hasOwnProperty.call(data, k)) payload[k] = data[k];
        }
        window.dataLayer.push(payload);
      }
    } catch (_) {}
    try {
      if (window.dluxJourney && typeof window.dluxJourney.track === 'function') {
        var jdata = { promo_location: PROMO_LOCATION };
        for (var k2 in data) {
          if (Object.prototype.hasOwnProperty.call(data, k2)) jdata[k2] = data[k2];
        }
        window.dluxJourney.track(type, jdata);
      }
    } catch (_) {}
    if (type === 'promo_copy_click' && typeof window.fbq === 'function') {
      try {
        window.fbq('trackCustom', 'PromoCopyClick', { promo_code: data && data.promo_code });
      } catch (_) {}
    }
  }

  function setState(container, state) {
    container.setAttribute('data-promo-state', state);
    var btn = container.querySelector('[data-dlux-cart-promo-apply]');
    if (!btn) return;
    var defaultLabel = btn.querySelector('[data-label-default]');
    var appliedLabel = btn.querySelector('[data-label-applied]');
    if (state === 'applied') {
      if (defaultLabel) defaultLabel.hidden = true;
      if (appliedLabel) appliedLabel.hidden = false;
      // Stay clickable so customers can re-copy. No aria-pressed (not a toggle).
      btn.disabled = false;
      btn.removeAttribute('aria-pressed');
    } else {
      if (defaultLabel) defaultLabel.hidden = false;
      if (appliedLabel) appliedLabel.hidden = true;
      btn.disabled = state === 'loading';
      btn.removeAttribute('aria-pressed');
    }
  }

  // Clipboard rejection is not silent any more. iOS in-app browsers reject the async
  // write, so reveal the code in a readonly input and select it for tap and hold.
  function revealFallback(container) {
    var wrap = container.querySelector('[data-dlux-cart-promo-fallback]');
    if (!wrap) return;
    wrap.hidden = false;
    var input = wrap.querySelector('[data-dlux-cart-promo-input]');
    if (!input) return;
    try {
      input.focus({ preventScroll: true });
      if (typeof input.setSelectionRange === 'function') {
        input.setSelectionRange(0, String(input.value).length);
      }
      input.select();
    } catch (_) {}
  }

  function hideFallback(container) {
    var wrap = container.querySelector('[data-dlux-cart-promo-fallback]');
    if (wrap) wrap.hidden = true;
  }

  function copyDiscount(container) {
    var code = container.getAttribute('data-promo-code');
    if (!code) return;
    setState(container, 'loading');
    var writePromise;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      writePromise = navigator.clipboard.writeText(code);
    } else {
      // Fallback for older browsers / insecure contexts: hidden textarea + execCommand.
      writePromise = new Promise(function (resolve, reject) {
        try {
          var ta = document.createElement('textarea');
          ta.value = code;
          ta.setAttribute('readonly', '');
          ta.style.position = 'absolute';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          var ok = document.execCommand('copy');
          document.body.removeChild(ta);
          ok ? resolve() : reject(new Error('execCommand returned false'));
        } catch (e) {
          reject(e);
        }
      });
    }
    writePromise
      .then(function () {
        pushAnalytics('promo_copy_click', {
          promo_code: code,
          copy_method: navigator.clipboard && navigator.clipboard.writeText ? 'clipboard_api' : 'exec_command',
        });
        hideFallback(container);
        setState(container, 'applied'); // reuse the existing "Copied" visual state
        // Revert button to default after 2 seconds so the customer can copy again.
        setTimeout(function () {
          if (container.getAttribute('data-promo-state') === 'applied') {
            setState(container, 'idle');
          }
        }, 2000);
      })
      .catch(function (err) {
        pushAnalytics('promo_copy_error', {
          promo_code: code,
          error: err && err.message ? err.message : 'unknown',
        });
        // Was setState(container, 'idle'), which showed the shopper nothing at all.
        setState(container, 'fallback');
        revealFallback(container);
      });
  }

  function init(container) {
    if (!container || container.__dluxCartPromoInit) return;
    container.__dluxCartPromoInit = true;
    setState(container, 'idle');
    var btn = container.querySelector('[data-dlux-cart-promo-apply]');
    if (btn) {
      btn.addEventListener('click', function () {
        // Allow re-copying even after "Copied" state is shown.
        copyDiscount(container);
      });
    }
  }

  // promo_view — fire once per cart-drawer open. We watch the
  // <cart-drawer> custom element for its `active` class flip and
  // re-arm whenever it closes so the next open fires again.
  var promoViewFiredThisOpen = false;
  function firePromoViewIfVisible() {
    if (promoViewFiredThisOpen) return;
    var container = document.querySelector('[data-dlux-cart-promo]');
    if (!container) return;
    var code = container.getAttribute('data-promo-code');
    if (!code) return;
    promoViewFiredThisOpen = true;
    pushAnalytics('promo_view', { promo_code: code });
  }
  function watchCartDrawerOpen() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var wasActive = drawer.classList.contains('active');
    if (wasActive) firePromoViewIfVisible();
    var classObs = new MutationObserver(function () {
      var active = drawer.classList.contains('active');
      if (active && !wasActive) {
        promoViewFiredThisOpen = false;
        // Wait a frame so the promo block has rendered.
        requestAnimationFrame(firePromoViewIfVisible);
      } else if (!active && wasActive) {
        promoViewFiredThisOpen = false;
      }
      wasActive = active;
    });
    classObs.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  function initAll() {
    document.querySelectorAll('[data-dlux-cart-promo]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
      watchCartDrawerOpen();
    });
  } else {
    initAll();
    watchCartDrawerOpen();
  }

  // Re-init after Shopify rerenders the cart drawer (e.g. after add-to-cart).
  document.addEventListener('cart:refresh', initAll);
  document.addEventListener('cart:updated', initAll);
  var observer = new MutationObserver(function () {
    initAll();
  });
  var drawer = document.getElementById('CartDrawer');
  if (drawer) {
    observer.observe(drawer, { childList: true, subtree: true });
  }
})();
